import type { ComponentProps, ReactNode } from 'react'

import Group from 'components/utils/Group'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import PriceRowPriceCaptionLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceCaptionLoadingSkeleton'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowValueDiscountWithCaptionLoadingSkeleton from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaptionLoadingSkeleton'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'

type LoyaltyEarnProps = ComponentProps<typeof LoyaltyPointsEarnWithBreakdownModal>

interface Props {
  offer: App.Offer
  saveAmount?: number
  urgencyLabels?: Array<App.OfferUrgencyLabel>
  caption?: ReactNode
  priceContent: ReactNode
  leadingContent?: ReactNode
  afterValueContent?: ReactNode
  labelExtras?: ReactNode
  footerContent?: ReactNode
  originalValue?: number
  discountPercentage?: number
  loyaltyCalculationRequests: LoyaltyEarnProps['calculationRequests']
  loyaltyCalculationType: LoyaltyEarnProps['calculationType']
  loyaltyItemTitle?: string
  loyaltyPointsToApply?: number
  loyaltyBurnPriceInPoints?: number
  loading?: boolean
  className?: string
}

/**
 * Shared commercial framing for a Flash hotel package price.
 *
 * The offer page and Trip Planner deliberately own different room layouts and
 * actions, but the customer-facing saving, voucher, value and loyalty treatment
 * should stay in the same order and follow the same component behaviour.
 */
function FlashHotelPackagePriceBlock({
  offer,
  saveAmount,
  urgencyLabels,
  caption,
  priceContent,
  leadingContent,
  afterValueContent,
  labelExtras,
  footerContent,
  originalValue,
  discountPercentage,
  loyaltyCalculationRequests,
  loyaltyCalculationType,
  loyaltyItemTitle,
  loyaltyPointsToApply,
  loyaltyBurnPriceInPoints,
  loading = false,
  className,
}: Props) {
  if (loading) {
    return (
      <VerticalSpacer className={className} gap={4} data-testid="pricing-loading">
        <PriceRowPriceCaptionLoadingSkeleton />
        <PriceRowPriceLoadingSkeleton size="L" />
        <PriceRowValueDiscountWithCaptionLoadingSkeleton />
      </VerticalSpacer>
    )
  }

  return (
    <VerticalSpacer className={className} gap={12} data-testid="pricing">
      <VerticalSpacer gap={4}>
        <LabelGroup>
          <OfferLabels
            offer={offer}
            saveAmount={saveAmount}
            urgencyLabels={urgencyLabels}
          />
          {labelExtras}
        </LabelGroup>

        <div>
          {leadingContent}
          <VerticalSpacer gap={8}>
            <Group direction="vertical" horizontalAlign="start" noDisplayWhenEmpty>
              <LuxPlusOfferVoucherCallout offer={offer} mode="offer" align="start" />
            </Group>
            {caption}
          </VerticalSpacer>

          {priceContent}

          {originalValue !== undefined && (
            <PriceRowValueDiscountWithCaption
              data-testid="value-box"
              discountPercentage={discountPercentage}
              size="M"
              originalValue={originalValue}
            />
          )}

          {afterValueContent}
        </div>

        {footerContent}
      </VerticalSpacer>

      <LoyaltyPointsEarnWithBreakdownModal
        calculationRequests={loyaltyCalculationRequests}
        calculationType={loyaltyCalculationType}
        itemTitle={loyaltyItemTitle}
        pointsToApply={loyaltyPointsToApply}
        burnPriceInPoints={loyaltyBurnPriceInPoints}
      />
    </VerticalSpacer>
  )
}

export default FlashHotelPackagePriceBlock
